import type { SupabaseClient } from '@supabase/supabase-js';
import { showToast, openErrorModal } from '@vario-connect/frontend-design';
import { loadMappingSources, type FlatSource } from './mapping-helpers.js';
import { infoBadge } from '../../lib/info-badge.js';

// Volltext-Erklärung der Mapping-Modi (Info-Badge im Kopf) - die Eingabefelder selbst sind zu
// kurz für den Beispieltext, deshalb hier ungekürzt und dauerhaft sichtbar.
const MODE_HELP =
  'Modi je Feld: „Vario-Feld" übernimmt den Wert eines Vario-Felds. ' +
  '„Fester Wert" überträgt genau den eingegebenen Text (ohne Platzhalter). ' +
  '„Formel" ersetzt Platzhalter in geschweiften Klammern durch Vario-Werte, Text dazwischen bleibt: ' +
  '{name} = Artikelname, {article.manufacturerRef.label} = Hersteller, {listing.grossSalesPrice} = Brutto-Preis. ' +
  'So ergibt „{article.manufacturerRef.label} - {name}" z. B. „ACME - Laufschuh". ' +
  '„Standard"/„Nicht gemappt": kein globales Mapping - der Artikel liefert seinen eigenen Wert.';

// ============================================================================
// Zentrales Artikel-Feld-Mapping (global je Mandant) - Abschnitt im Einstellungen-Tab.
// Ersetzt das frühere per-Artikel „Mapping"/„Vorlagen"-Konstrukt. Vier Modi
// (Vario-Feld / fester Wert / Formel / nicht gemappt) über die 6 skalaren Produktfelder.
// Persistenz: gambio_get_global_mapping / gambio_save_global_mapping.
// ============================================================================

export interface ArticleMappingCtx {
  supabase: SupabaseClient;
  supabaseUrl: string;
  session: { token: string };
  storefront: string;
}

interface FieldRow { key: string; label: string; hint: string; }
interface SavedField {
  gambio_field_key: string; field_group: string; source_type: string;
  vario_path: string | null; fixed_value: unknown; template: string | null;
}

const FIELDS: FieldRow[] = [
  { key: 'name',         label: 'Titel',        hint: 'Produktname in Gambio' },
  { key: 'description',  label: 'Beschreibung', hint: 'Langtext' },
  { key: 'priceNet',     label: 'Preis (netto)',hint: 'Basispreis netto' },
  { key: 'ean',          label: 'EAN',          hint: 'GTIN/EAN' },
  { key: 'sku',          label: 'Modell/SKU',   hint: 'Artikelnummer' },
  { key: 'manufacturer', label: 'Hersteller',   hint: 'Herstellername' },
];

const MODES: Array<{ value: string; label: string }> = [
  { value: 'unmapped',    label: 'Nicht gemappt' },
  { value: 'vario_path',  label: 'Vario-Feld' },
  { value: 'fixed_value', label: 'Fester Wert' },
  { value: 'template',    label: 'Formel {feld}' },
];

// Zusatzfelder: KEIN „Nicht gemappt" (global wirkungslos - ohne Regel exportiert das Feld ohnehin
// seinen Artikel-Wert). Stattdessen „Standard (Zusatzfeld-Wert)" als ehrlicher Default (= keine
// globale Regel; wird beim Speichern nicht persistiert, exakt wie unmapped bei Skalaren).
const ADD_MODES: Array<{ value: string; label: string }> = [
  { value: 'standard',    label: 'Standard (Zusatzfeld-Wert)' },
  { value: 'vario_path',  label: 'Vario-Feld' },
  { value: 'fixed_value', label: 'Fester Wert' },
  { value: 'template',    label: 'Formel {feld}' },
];

function esc(s: string | null | undefined): string {
  if (s == null) return '';
  return String(s).replaceAll('&','&amp;').replaceAll('<','&lt;').replaceAll('>','&gt;').replaceAll('"','&quot;').replaceAll("'",'&#39;');
}

export async function renderArticleMapping(host: HTMLElement, ctx: ArticleMappingCtx): Promise<void> {
  host.innerHTML = `<div class="text-grey">Lade Artikel-Mapping …</div>`;
  const { data, error } = await ctx.supabase.rpc('gambio_get_global_mapping');
  if (error) { host.innerHTML = `<div class="install-status install-status--error">Mapping nicht ladbar: ${esc(error.message)}</div>`; return; }
  const saved = new Map<string, SavedField>(
    (((data as { fields?: SavedField[] } | null)?.fields) ?? []).map((f) => [f.gambio_field_key, f]),
  );

  // Zusatzfeld-Katalog (shop-global) via Edge-Fn laden → globaler 'additional'-Abschnitt.
  const addCatalog: FieldRow[] = [];
  // Gruende, falls der Zusatzfeld-Katalog nicht gelesen werden konnte — sonst fehlt der Abschnitt
  // stillschweigend und sieht aus wie „Shop hat keine Zusatzfelder" (Vorfall 2026-08-18).
  const addWarnings: string[] = [];
  try {
    const res = await fetch(`${ctx.supabaseUrl}/functions/v1/gambio-listing-schema`, {
      method: 'POST', headers: { Authorization: `Bearer ${ctx.session.token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ storefront: ctx.storefront }),
    });
    if (res.ok) {
      const schema = (await res.json()) as {
        fields?: Array<{ key: string; label: string; group: string }>;
        warnings?: string[];
      };
      addWarnings.push(...(schema.warnings ?? []));
      const seen = new Set<number>();
      for (const f of schema.fields ?? []) {
        if (f.group !== 'additional') continue;
        const m = f.key.match(/^additional:f(\d+)(?::[a-z]{2,})?$/i);
        if (!m) continue;
        const fid = Number(m[1]);
        if (seen.has(fid)) continue;
        seen.add(fid);
        addCatalog.push({ key: `f${fid}`, label: f.label.replace(/\s*\([a-z]{2,}\)\s*$/i, '').trim(), hint: 'Gambio-Zusatzfeld' });
      }
      addCatalog.sort((a, b) => Number(a.key.slice(1)) - Number(b.key.slice(1)));
    } else {
      addWarnings.push(`Zusatzfeld-Katalog nicht ladbar (HTTP ${res.status}).`);
    }
  } catch (e) {
    addWarnings.push(`Zusatzfeld-Katalog nicht ladbar: ${(e as Error).message}`);
  }

  // Vario-Quellen dynamisch laden (gambio-mapping-sources) - kein articleId (global): generische
  // Listing-/Artikel-/EAV-Quellen. Best-effort: leere Liste → Dropdown nur mit „— wählen —".
  const sources: FlatSource[] = await loadMappingSources(
    { supabaseUrl: ctx.supabaseUrl, session: ctx.session },
    { storefront: ctx.storefront },
  );
  const optsHtml = (vp: string): string =>
    sources.map((o) => {
      const suffix = o.example ? ` - ${o.example}` : '';
      return `<option value="${esc(o.path)}"${o.path === vp ? ' selected' : ''}>${esc(o.label + suffix)}</option>`;
    }).join('');

  // Spaltenreihenfolge: Modus ZUERST, dann Vario-Quelle, dann Gambio-Zielfeld. Der Modus steuert,
  // welches Eingabefeld in der Vario-Spalte erscheint - er gehoert deshalb links davor, nicht
  // dahinter (Betreiber-Ansage 21.08.2026). Grid und Kopfzeile teilen dieselbe Definition, damit
  // sie nicht auseinanderlaufen.
  const AM_GRID = '150px 1fr 260px';
  const headHtml = (): string => `
    <div style="display:grid;grid-template-columns:${AM_GRID};gap:8px;padding:0 0 6px;border-bottom:1px solid var(--go-border,#e5e7eb);font-size:11px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:var(--go-muted,#6b7280);">
      <div>Modus</div><div>Vario</div><div>Gambio</div>
    </div>`;

  const rowHtml = (f: FieldRow): string => {
    const s = saved.get(f.key);
    const isAdd = /^f\d+$/.test(f.key);
    const modes = isAdd ? ADD_MODES : MODES;
    const st = s?.source_type ?? (isAdd ? 'standard' : 'unmapped');
    const vp = s?.vario_path ?? '';
    const fv = typeof s?.fixed_value === 'string' ? s.fixed_value : '';
    const tpl = s?.template ?? '';
    const opts = optsHtml(vp);
    return `
      <div class="am-row" data-field="${esc(f.key)}" style="display:grid;grid-template-columns:${AM_GRID};gap:8px;align-items:start;padding:6px 0;border-bottom:1px solid var(--go-border,#e5e7eb);">
        <div class="v-select"><select data-am-mode="${esc(f.key)}">
          ${modes.map((m) => `<option value="${m.value}"${m.value === st ? ' selected' : ''}>${m.label}</option>`).join('')}
        </select></div>
        <div>
          <div class="v-select" data-am-ctl="vario_path" data-for="${esc(f.key)}" hidden><select data-am-vp>
            <option value="">— Vario-Feld wählen —</option>${opts}
          </select></div>
          <div class="v-input" data-am-ctl="fixed_value" data-for="${esc(f.key)}" hidden style="width:100%;">
            <input type="text" data-am-fv placeholder="Fester Wert" value="${esc(fv)}" /></div>
          <div class="v-input" data-am-ctl="template" data-for="${esc(f.key)}" hidden style="width:100%;">
            <input type="text" data-am-tpl placeholder="z.B. {article.manufacturerRef.label} {name}" value="${esc(tpl)}" /></div>
          <div data-am-ctl="standard" data-for="${esc(f.key)}" hidden>
            <span class="text-grey" style="font-size:12px;">Wert wird je Artikel im Tab „Zusatzfelder" gepflegt.</span></div>
          <div data-am-ctl="unmapped" data-for="${esc(f.key)}" hidden>
            <span class="text-grey" style="font-size:12px;">Kein globales Feld-Mapping.</span></div>
        </div>
        <div><strong>${esc(f.label)}</strong><div class="text-grey" style="font-size:11px;">${esc(f.hint)}</div></div>
      </div>`;
  };

  host.innerHTML = `
    <div class="v-row v-row-sm" style="justify-content:space-between;align-items:center;margin:0 0 10px;">
      <div style="display:flex;align-items:center;gap:6px;">
        <h4 class="klst-detail-section" style="margin:0;">Artikel-Feld-Mapping</h4>
        ${infoBadge(MODE_HELP)}
      </div>
      <button type="button" class="v-button v-button--save" data-am-save>
        <span class="v-icon fa-regular fa-floppy-disk"></span> Artikel-Mapping speichern
      </button>
    </div>
    <h4 class="klst-detail-section" style="margin:0 0 6px;">Stammdaten</h4>
    <div class="am-fields">${headHtml()}${FIELDS.map(rowHtml).join('')}</div>
    ${addCatalog.length ? `
    <h4 class="klst-detail-section" style="margin:16px 0 6px;">Zusatzfelder</h4>
    <div class="am-fields am-additional">${headHtml()}${addCatalog.map(rowHtml).join('')}</div>` : ''}
    ${!addCatalog.length && addWarnings.length ? `
    <h4 class="klst-detail-section" style="margin:16px 0 6px;">Zusatzfelder</h4>
    <div class="v-banner v-banner-warn"><span class="v-icon fa-light fa-triangle-exclamation"></span>
      <div>${esc(addWarnings.join(' '))} Die im Shop gepflegten Felder sind nicht verloren.</div>
    </div>` : ''}`;

  const sync = (key: string, mode: string): void => {
    host.querySelectorAll<HTMLElement>(`[data-for="${CSS.escape(key)}"]`).forEach((el) => {
      const show = el.getAttribute('data-am-ctl') === mode;
      // Inline-display: `.v-select { display:inline-flex }` schlaegt sonst `[hidden]` → falsche
      // Steuer-Elemente (z. B. „Vario-Feld"-Dropdown) blieben neben der Vorlage-Anzeige sichtbar.
      el.hidden = !show;
      el.style.display = show ? '' : 'none';
    });
  };
  host.querySelectorAll<HTMLSelectElement>('[data-am-mode]').forEach((sel) => {
    const key = sel.getAttribute('data-am-mode')!;
    sel.addEventListener('change', () => sync(key, sel.value));
    sync(key, sel.value);
  });

  host.querySelector<HTMLButtonElement>('[data-am-save]')?.addEventListener('click', async () => {
    const fields: SavedField[] = [];
    host.querySelectorAll<HTMLElement>('.am-row').forEach((row) => {
      const key = row.getAttribute('data-field')!;
      const mode = row.querySelector<HTMLSelectElement>('[data-am-mode]')!.value;
      if (mode === 'unmapped' || mode === 'standard') return;
      const isAdditional = /^f\d+$/.test(key);
      const entry: SavedField = { gambio_field_key: key, field_group: isAdditional ? 'additional' : 'product_data_basic', source_type: mode, vario_path: null, fixed_value: null, template: null };
      if (mode === 'vario_path')  entry.vario_path  = row.querySelector<HTMLSelectElement>('[data-am-vp]')?.value || null;
      if (mode === 'fixed_value') entry.fixed_value = row.querySelector<HTMLInputElement>('[data-am-fv]')?.value ?? '';
      if (mode === 'template')    entry.template    = row.querySelector<HTMLInputElement>('[data-am-tpl]')?.value ?? '';
      fields.push(entry);
    });
    const { error: saveErr } = await ctx.supabase.rpc('gambio_save_global_mapping', { _fields: fields });
    if (saveErr) {
      openErrorModal({
        title: 'Speichern fehlgeschlagen',
        items: [{ message: saveErr.message }],
        context: { action: 'gambio_save_global_mapping', storefront: ctx.storefront },
        json: saveErr,
      });
      return;
    }
    showToast('Artikel-Mapping gespeichert.', 'success');
  });
}
